import {
  GetDiscountsResponse,
  GetDiscountsResponse_Discount,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/campaigns/v1/service.types';
import {useSessionStorage} from '@aboutyou/storage';
import React, {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from 'react';
import {useDiscounts} from '../hooks/useDiscounts';

const CAMPAIGN_KEY_SNAPSHOT_STORAGE_KEY = 'ay_co_discount';
const useSnapshotCampaignKey = () => {
  return useSessionStorage<
    | {
        campaignKey: string | null;
      }
    | undefined
  >({
    key: CAMPAIGN_KEY_SNAPSHOT_STORAGE_KEY,
    defaultValue: undefined,
    forcePersistValue_ILLEGALLY: true,
  });
};

export const useCreateSnapshotCampaign = () => {
  const [, update] = useSnapshotCampaignKey();
  return update;
};

export const useRequestSnapshotCampaignKey = () => {
  const [value] = useSnapshotCampaignKey();
  return {
    campaignKey: value?.campaignKey ?? null,
    isLoaded: value !== undefined,
  };
};

export type SelectedDiscountValue = GetDiscountsResponse_Discount | null;

const UserSelectedDiscountContext = createContext<{
  selectedDiscount: SelectedDiscountValue;
}>({
  selectedDiscount: null,
});

export const useSelectedDiscount = (): SelectedDiscountValue => {
  return useContext(UserSelectedDiscountContext).selectedDiscount;
};

export const UserSelectedDiscountProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const {data} = useDiscounts();

  const currentDiscount = useMemo(() => getCurrentDiscount(data), [data]);

  const value = useMemo(() => {
    return {
      selectedDiscount: currentDiscount,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDiscount?.id]);

  return (
    <UserSelectedDiscountContext.Provider value={value}>
      {children}
    </UserSelectedDiscountContext.Provider>
  );
};

export const useIsSelectedDiscountAppOnly = () => {
  const selectedDiscount = useSelectedDiscount();
  return (
    selectedDiscount?.campaign.type?.$case === 'reduction' &&
    selectedDiscount.campaign.type.reduction.isAppOnlyCampaign
  );
};

const getCurrentDiscount = (
  requestData: GetDiscountsResponse | undefined,
): SelectedDiscountValue => {
  if (!requestData) {
    return null;
  }
  const {discount} = requestData;
  return discount || null;
};
